import type { GameState, HeroData, RaceTick, UpdateEvent } from '../helpers/types';
import { heroesData } from '../settings/heroesData';

const getDisplayName = (id: string) => heroesData[id]?.name ?? id;

export type RaceRacerLog = {
    name: string;
    distance: number;
    position: number;
};

export type RaceTickLog = {
    raceId: string;
    gameState: GameState;
    raceTick: number;
    timestamp: number;
    racers: RaceRacerLog[];
};

export type RaceLog = {
    raceId: string;
    startedAt: number;
    updatedAt: number;
    ticks: RaceTickLog[];
};

export type RaceLoggerApi = {
    races: RaceLog[];
    logTick: (payload: RaceTick | UpdateEvent, racers: HeroData[]) => void;
    printReport: (raceId?: string) => void;
    getAll: () => RaceLog[];
    getRace: (raceId: string) => RaceLog | undefined;
    getCurrentRace: () => RaceLog | undefined;
    clear: () => void;
    export: () => string;
};

const cloneRace = (race: RaceLog): RaceLog => ({
    ...race,
    ticks: race.ticks.map((tick) => ({
        ...tick,
        racers: tick.racers.map((racer) => ({ ...racer })),
    })),
});

class RaceLogger implements RaceLoggerApi {
    races: RaceLog[] = [];
    private raceIndex = new Map<string, RaceLog>();
    private loggedTicks = new Set<string>();
    private printedRaces = new Set<string>();

    logTick(payload: RaceTick | UpdateEvent, racers: HeroData[]) {
        if (!payload.raceId) return;

        const tickKey = `${payload.raceId}:${payload.gameState}:${payload.raceTick}`;
        if (this.loggedTicks.has(tickKey)) return;

        const timestamp = Date.now();
        const race = this.getOrCreateRace(payload.raceId, timestamp);

        const tick: RaceTickLog = {
            raceId: payload.raceId,
            gameState: payload.gameState,
            raceTick: payload.raceTick,
            timestamp,
            racers: racers
                .map((racer) => ({
                    name: racer.name,
                    distance: racer.distance,
                    position: racer.position,
                }))
                .sort((a, b) => a.position - b.position),
        };

        race.ticks.push(tick);
        race.updatedAt = timestamp;
        this.loggedTicks.add(tickKey);
    }

    printReport(raceId?: string) {
        const race = raceId ? this.raceIndex.get(raceId) : this.races.at(-1);
        if (!race) return;
        this.printRaceTable(race);
    }

    getAll() {
        return this.races.map(cloneRace);
    }

    getRace(raceId: string) {
        const race = this.raceIndex.get(raceId);

        return race ? cloneRace(race) : undefined;
    }

    getCurrentRace() {
        const race = this.races.at(-1);

        return race ? cloneRace(race) : undefined;
    }

    clear() {
        this.races = [];
        this.raceIndex.clear();
        this.loggedTicks.clear();
        this.printedRaces.clear();
    }

    export() {
        return JSON.stringify(this.getAll());
    }

    private getOrCreateRace(raceId: string, timestamp: number) {
        const existingRace = this.raceIndex.get(raceId);

        if (existingRace) {
            return existingRace;
        }

        const race: RaceLog = {
            raceId,
            startedAt: timestamp,
            updatedAt: timestamp,
            ticks: [],
        };

        this.races.push(race);
        this.raceIndex.set(raceId, race);

        return race;
    }

    private printRaceTable(race: RaceLog) {
        if (this.printedRaces.has(race.raceId)) return;

        const raceTicks = race.ticks.filter((tick) => tick.gameState === 'RACE');
        if (raceTicks.length === 0) return;

        console.group(`Race log: ${race.raceId}`);
        console.table(
            Object.fromEntries(
                raceTicks.map((tick) => [
                    tick.raceTick,
                    Object.fromEntries(
                        tick.racers.map((racer) => [
                            `place ${racer.position}`,
                            `${getDisplayName(racer.name)}: ${racer.distance}`,
                        ]),
                    ),
                ]),
            ),
        );

        const finalRacers = [...raceTicks[raceTicks.length - 1].racers].sort(
            (a, b) => a.position - b.position,
        );
        console.table(
            Object.fromEntries(
                finalRacers.map((racer) => [
                    racer.position,
                    { name: getDisplayName(racer.name), distance: racer.distance },
                ]),
            ),
        );
        console.groupEnd();

        this.printedRaces.add(race.raceId);
    }
}

export const raceLogger = new RaceLogger();

if (typeof window !== 'undefined') {
    window.raceLogger = raceLogger;
}

declare global {
    interface Window {
        raceLogger: RaceLoggerApi;
    }
}
